import { Vue, Component, Ref } from 'vue-property-decorator';
import * as DB from 'interfaces/database';
import { BModal } from 'bootstrap-vue';
import mitt from 'mitt';
import Swal from 'sweetalert2';
import { TabEvent } from 'interfaces/app';
import { httpClient } from 'utils/http';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.RegionModel>>();
@Component({
	components: {
		TabulatorBtn,
	},
})
export default class RegionTable extends Vue.extend(Template) {
	@Ref('addregion-modal')
	private readonly modal!: BModal;

	@Ref('regionTable')
	private readonly tableReference!: HTMLDivElement;

	private regiondata = {} as Pick<DB.RegionModel, 'name' | 'locale'>;

	private isLoaded = false;

	private isLoading = false;

	private table?: Tabulator;

	private columnDefs: ColumnDefinitionExtended[] = []

	private data: DB.HyperlinkModel[] & { regionname?: string} = [];

	private get loggedIn(): boolean {
		return this.$auth.isAuthenticated;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'name',
				title: 'Name',
			},
			{
				title: 'Locale',
				field: 'locale',
			},
			{
				title: 'Logo desktop',
				field: 'logo_desktop',
				formatter: 'image',
				formatterParams: {
					height: '150px',
				},
			},
			{
				title: 'Logo mobile',
				field: 'logo_mobile',
				formatter: 'image',
				formatterParams: {
					height: '150px',
				},
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.ProductCategoryModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					instance.$mount();

					return (instance.$el as HTMLElement);
				},
			},
		];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deleteRegion,
		);
		eventBus.on(
			'editItem',
			this.editRegion,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeDestroy(): void {
		eventBus.off(
			'deleteItem',
			this.deleteRegion,
		);
		eventBus.off(
			'editItem',
			this.editRegion,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			const { data } = await httpClient.get<DB.RegionModel[]>(`/api/region?${parameter}`);
			this.table?.setData(data);
		} catch (err: any) {
			this.$bvToast.toast(
				`${err.message}`,
				{
					solid: true,
					variant: 'danger',
				},
			);
		} finally {
			this.table?.clearAlert();
		}
	}

	protected addRegion(): void {
		this.isLoaded = true;
		httpClient.post(
			'/api/region',
			{ ...this.regiondata },
		).then((res) => {
			this.modal.hide();

			this.regiondata = {
				name: '',
				locale: '',
			};
			this.$emit(
				'routeRegion',
				res.data.id,
			);
			return undefined;
		})
			.finally(() => {
				this.isLoaded = false;
			})
			.catch((err) => {
				this.$bvToast.toast(
					`${err.message}`,
					{
						solid: true,
						variant: 'danger',
					},
				);
			});
	}

	private editRegion(data: TabEvent<DB.RegionModel>['editItem']): void {
		this.$emit(
			'routeRegion',
			data.id,
		);
	}

	private deleteRegion(data: TabEvent<DB.RegionModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading');
				// eslint-disable-next-line promise/no-nesting
				httpClient.delete(`/api/region/${data.id}`).then(() => {
					this.$bvToast.toast(
						'Item Deleted',
						{
							solid: true,
							variant: 'success',
						},
					);
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$bvToast.toast(
						`${err.message}`,
						{
							solid: true,
							variant: 'danger',
						},
					);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$bvToast.toast(
				`${err.message}`,
				{
					solid: true,
					variant: 'danger',
				},
			);
		});
	}
}
